import type { WorkWeight } from '@vwt/contracts';
import { createHashLike } from './hash.js';

/**
 * Work-item weighting with TypeSafe Jev (ADR-0058). Jev answers one Noul (information
 * sufficiency) and four 0–4 Scores per completion episode; only those raw answers are stored.
 * Points are derived here at read time, so a weight change needs no new inference.
 */

export const WEIGHT_DIMENSIONS = ['scope', 'uncertainty', 'risk', 'coordination'] as const;
export type WeightDimension = (typeof WEIGHT_DIMENSIONS)[number];

/** Questions sent to Jev. Any text change is a new question version and a rescore of recent episodes. */
export const WEIGHT_QUESTIONS = {
  info_sufficient: {
    type: 'noul',
    instructions:
      'Does `task` (title, description, acceptance criteria, progress and linked pull requests with their descriptions and diff size) contain enough concrete detail to judge how much engineering work it required? A bare title like "Fix bug" does not.',
  },
  scope: {
    type: 'score',
    instructions: 'How broad is the change described in `task`?',
    criteria: [
      'A one-line or config tweak in a single place',
      'A small change in one module or screen',
      'Several files across one service, maybe a small UI plus API change',
      'A new capability across backend, frontend and data (new tables, routes, screens)',
      'A large subsystem or cross-repository change with many new tables, routes and screens',
    ],
  },
  uncertainty: {
    type: 'score',
    instructions: 'How much unknown had to be resolved to deliver `task`?',
    criteria: [
      'Fully specified; the fix or change is obvious',
      'Mostly clear, minor details to decide',
      'Some design decisions or investigation needed',
      'Significant design, spikes or rejected alternatives',
      'Open-ended research or root cause unknown at the start',
    ],
  },
  risk: {
    type: 'score',
    instructions: 'How risky is `task` to production, data, money or security?',
    criteria: [
      'Cosmetic or internal-only, no production impact if wrong',
      'Low-risk user-facing change',
      'Touches important flows but is easy to roll back',
      'Database migration, permissions/authorization, or payments logic',
      'Irreversible data change or critical security/money path',
    ],
  },
  coordination: {
    type: 'score',
    instructions: 'How much coordination with other people, teams or systems did `task` require?',
    criteria: [
      'None, one person end to end',
      'A review or a quick confirmation',
      'Depends on another team or an external system',
      'Several teams or systems must change in order',
      'Multi-team rollout with staged deploys or migrations across services',
    ],
  },
} as const;

export const questionsVersion = (questions: unknown) => `q-${createHashLike(JSON.stringify(questions)).slice(0, 8)}`;
export const QUESTIONS_VERSION = questionsVersion(WEIGHT_QUESTIONS);

/**
 * Weights in twentieths (scope .40, uncertainty .20, risk .35, coordination .05), integers so the
 * bucket edges are exact. Changing one, or how points add up, bumps WEIGHTS_VERSION (the metric's formula version).
 * Version 2: a reopened item counts once, at its highest score (owner decision 2026-10-22).
 */
export const WEIGHTS: Readonly<Record<WeightDimension, number>> = { scope: 8, uncertainty: 4, risk: 7, coordination: 1 };
export const WEIGHTS_VERSION = '2';
export const POINTS = [1, 2, 3, 5, 8, 13] as const;
/** info_sufficient below this leaves the episode unscored (owner decision). */
export const INFO_THRESHOLD = 0.3;

export type WeightDimensions = Record<WeightDimension, { score: number; confidence: number }>;

export const statusForInfo = (infoSufficient: number): 'SCORED' | 'INSUFFICIENT_INFO' => (infoSufficient >= INFO_THRESHOLD ? 'SCORED' : 'INSUFFICIENT_INFO');

/**
 * Points of a stored score: NO_EVIDENCE is 0, SCORED maps to a bucket, anything else (or a
 * malformed dimension) is unavailable (null). Bucket = floor(6 · Σ wᵢ·sᵢ / (20 · 4)); the epsilon
 * absorbs float noise from fractional scores so an exact edge never falls one bucket short.
 */
export function pointsFor(row: { status: string; dimensions: unknown }): number | null {
  if (row.status === 'NO_EVIDENCE') return 0;
  if (row.status !== 'SCORED' || !row.dimensions || typeof row.dimensions !== 'object') return null;
  let sum = 0;
  for (const d of WEIGHT_DIMENSIONS) {
    const score = (row.dimensions as Record<string, { score?: unknown } | undefined>)[d]?.score;
    if (typeof score !== 'number' || !Number.isFinite(score)) return null;
    sum += WEIGHTS[d] * Math.min(4, Math.max(0, score));
  }
  return POINTS[Math.min(POINTS.length - 1, Math.floor((POINTS.length * sum) / MAX_WEIGHTED_SUM + 1e-9))]!;
}
/** Σ weight × top score (4): the weighted sum of an item that maxes every dimension. */
const MAX_WEIGHTED_SUM = 4 * WEIGHT_DIMENSIONS.reduce((total, d) => total + WEIGHTS[d], 0);

/**
 * Whether an episode's item may be sent to Jev right now: LIVE, visible to the whole company,
 * confirmed, not canceled, and not a Vammo Apps deploy record. Archived work still counts: archiving
 * organizes, it does not undo work (owner decision). Rechecked before each call.
 * Same predicate as the sweep's candidate SQL and the report's reason CASE (services/work-weights.ts, services/reports.ts).
 */
export function isWeightEligible(item: { ingestionMode: string; visibility: string; restrictedBoundaryIds: readonly string[]; associationStatus: string; phase: string; createdChannel: string }, episode: { ingestionMode: string; completionBasis: string | null }): boolean {
  return (
    item.ingestionMode === 'LIVE' &&
    episode.ingestionMode === 'LIVE' &&
    episode.completionBasis !== 'IMPORTED_SOURCE' &&
    item.visibility === 'COMPANY' &&
    item.restrictedBoundaryIds.length === 0 &&
    item.associationStatus === 'CONFIRMED' &&
    item.phase !== 'CANCELED' &&
    item.phase !== 'EXPIRED' &&
    item.createdChannel !== 'VAMMO_APPS'
  );
}

/** The sweep weighs episodes completed within this window, up to this many attempts each. */
export const WEIGHT_LOOKBACK_MS = 30 * 86_400_000;
export const WEIGHT_MAX_ATTEMPTS = 3;

/** A counted episode of leaf `leafId`: its latest final score's status, else its current-version row's status (or null), and that row's attempts. */
export interface WeightEpisode {
  leafId: string;
  status: string | null;
  dimensions: unknown;
  attempts: number | null;
  completedAt: Date;
}

/**
 * Whether an episode counts for its item: it has a score row, or the sweep can still reach it. One
 * completed earlier with no row at all (typically before weighting existed) is ignored, by the display
 * and the report alike.
 */
export const isCountedEpisode = (e: { status: string | null; completedAt: Date }, now: Date): boolean => e.status !== null || e.completedAt.getTime() >= now.getTime() - WEIGHT_LOOKBACK_MS;

/**
 * Points shown on a work item (ADR-0058 "Display"): each leaf counts once, at the highest points among
 * its counted episodes (a reopen never adds points; owner decision 2026-10-22), and a parent sums its
 * eligible leaves. An episode is scored (points, 0 when NO_EVIDENCE), unscored (insufficient
 * information, out of attempts, or a failed row the sweep no longer reaches) or pending. A leaf with a
 * score is PARTIAL while another of its episodes is pending, since that one could raise the maximum.
 * An episode older than the sweep reaches with no row at all (typically completed before weighting
 * existed) is ignored; when every episode is ignored the item shows nothing (NOT_ELIGIBLE).
 */
export function foldWorkWeight(item: { eligible: boolean; isParent: boolean; episodes: readonly WeightEpisode[] }, now: Date): WorkWeight {
  const { isParent } = item;
  if (!item.eligible && !isParent) return { points: null, status: 'NOT_ELIGIBLE', isParent };
  if (!item.episodes.length) return { points: null, status: 'OPEN', isParent };
  const episodes = item.episodes.filter((e) => isCountedEpisode(e, now));
  if (!episodes.length) return { points: null, status: 'NOT_ELIGIBLE', isParent };
  const leaves = new Map<string, { max: number | null; pending: boolean }>();
  for (const e of episodes) {
    const leaf = leaves.get(e.leafId) ?? { max: null, pending: false };
    leaves.set(e.leafId, leaf);
    const p = pointsFor({ status: e.status ?? '', dimensions: e.dimensions });
    if (p !== null) {
      leaf.max = Math.max(leaf.max ?? 0, p);
      continue;
    }
    // A final row without points (or a malformed one) is never retried, nor is an exhausted or out-of-window episode.
    const settled = e.status === 'SCORED' || e.status === 'NO_EVIDENCE' || e.status === 'INSUFFICIENT_INFO' || (e.status === 'FAILED' && (e.attempts ?? 0) >= WEIGHT_MAX_ATTEMPTS);
    if (!settled && e.completedAt.getTime() >= now.getTime() - WEIGHT_LOOKBACK_MS) leaf.pending = true;
  }
  const all = [...leaves.values()];
  const scored = all.filter((l) => l.max !== null);
  const points = scored.reduce((sum, l) => sum + l.max!, 0);
  if (scored.length === all.length && !all.some((l) => l.pending)) return { points, status: 'SCORED', isParent };
  if (scored.length) return { points, status: 'PARTIAL', isParent };
  return { points: null, status: all.some((l) => l.pending) ? 'PENDING' : 'UNSCORED', isParent };
}

/**
 * Link and progress evidence belongs to an episode when created in (previous edge, edge], where
 * an episode's edge is its completion + 15 minutes, or its reopen when that comes first. Work done
 * after a quick reopen therefore counts for the next episode, and windows never overlap.
 */
export const EVIDENCE_GRACE_MS = 15 * 60_000;
type EpisodeTimes = { completedAt: Date; reopenedAt: Date | null };
const windowEdge = (e: EpisodeTimes): Date => {
  const graceEnd = new Date(e.completedAt.getTime() + EVIDENCE_GRACE_MS);
  return e.reopenedAt && e.reopenedAt < graceEnd ? e.reopenedAt : graceEnd;
};
export function evidenceWindow(previous: EpisodeTimes | null, episode: EpisodeTimes): { after: Date | null; until: Date } {
  return { after: previous ? windowEdge(previous) : null, until: windowEdge(episode) };
}

export const STATE_LIMITS = { description: 4000, progress: 6, progressChars: 800, pullRequests: 5, bodyChars: 1000, paths: 15 } as const;

export interface WeightStateInput {
  title: string;
  type: string;
  description: string;
  acceptanceCriteria: unknown;
  /** Newest first. */
  progress: string[];
  /** Newest first. */
  pullRequests: Array<{ repository: string; number: number; title: string; additions: number | null; deletions: number | null; changedFiles: number | null; changedPaths: string[]; body: string | null }>;
}

/** The `task` Jev reads, truncated to fixed limits so cost per item is bounded. */
export function buildWeightState(input: WeightStateInput) {
  const criteria = Array.isArray(input.acceptanceCriteria) ? (input.acceptanceCriteria as Array<{ text?: unknown }>).map((c) => c?.text).filter((t): t is string => typeof t === 'string') : [];
  return {
    title: input.title,
    type: input.type,
    description: input.description.slice(0, STATE_LIMITS.description),
    acceptanceCriteria: criteria,
    progress: input.progress.slice(0, STATE_LIMITS.progress).map((p) => p.slice(0, STATE_LIMITS.progressChars)),
    pullRequests: input.pullRequests.slice(0, STATE_LIMITS.pullRequests).map((p) => ({
      repository: p.repository,
      number: p.number,
      title: p.title,
      additions: p.additions,
      deletions: p.deletions,
      changedFiles: p.changedFiles,
      changedPaths: p.changedPaths.slice(0, STATE_LIMITS.paths),
      body: p.body === null ? null : p.body.slice(0, STATE_LIMITS.bodyChars),
    })),
  };
}
export type WeightState = ReturnType<typeof buildWeightState>;

/**
 * Provider failure classes: `retryable` (timeout, 5xx/529), `rate_limited` (429: retry next
 * sweep, stop this one, attempt refunded), `credential` (401: same as a rate limit), `refused`
 * (any other 4xx such as 403/400/404: stop this sweep, the attempt counts) and `invalid` (422,
 * malformed answer: no retry, the sweep goes on).
 */
export class WeightJudgeError extends Error {
  constructor(
    readonly kind: 'retryable' | 'rate_limited' | 'credential' | 'refused' | 'invalid',
    message: string,
  ) {
    super(message);
    this.name = 'WeightJudgeError';
  }
}
